/**
 * Pitch-preserving time stretch (WSOLA): windows of the source are
 * overlap-added at a fixed output hop, each taken from near its nominal
 * position (start + output position × rate) where it best continues the
 * previous window. Positions stay within SEARCH of nominal, so stretched
 * audio keeps to the playback clock.
 *
 * @module
 */

const WINDOW = 1536;
const HOP = WINDOW / 2;
const SEARCH = 480;
/** Similarity is judged on every DECIMATE-th sample of a mono mix. */
const DECIMATE = 4;

/** Periodic Hann: windows at 50% overlap sum to one. */
const HANN = Float32Array.from({ length: WINDOW }, (_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / WINDOW));

export class Wsola {
  /** Nominal source position of the next window. */
  private nominal: number;
  /** Source position of the last window used, or null before the first. */
  private last: number | null = null;
  /** Second half of the last window, still to be added to. */
  private readonly tail: Float32Array[];

  /**
   * @param channels decoded source samples, one array per channel
   * @param mono their sum ({@link monoMix})
   * @param start source sample to start from
   */
  constructor(
    private readonly channels: readonly Float32Array[],
    private readonly mono: Float32Array,
    private readonly rate: number,
    start: number,
  ) {
    this.nominal = start;
    this.tail = channels.map(() => new Float32Array(HOP));
  }

  /** The next `frames` output samples per channel (a multiple of the hop, rounded up). */
  render(frames: number): Float32Array[] {
    const hops = Math.ceil(frames / HOP);
    const out = this.channels.map(() => new Float32Array(hops * HOP));
    for (let h = 0; h < hops; h++) {
      const at = this.pick(Math.round(this.nominal));
      for (let c = 0; c < this.channels.length; c++) {
        const src = this.channels[c];
        const tail = this.tail[c];
        const dst = out[c];
        if (!src || !tail || !dst) continue;
        for (let i = 0; i < HOP; i++) {
          dst[h * HOP + i] = (tail[i] ?? 0) + (src[at + i] ?? 0) * (HANN[i] ?? 0);
          tail[i] = (src[at + HOP + i] ?? 0) * (HANN[HOP + i] ?? 0);
        }
      }
      this.last = at;
      this.nominal += HOP * this.rate;
    }
    return out;
  }

  /** The window start near `nominal` that best continues the last window. */
  private pick(nominal: number): number {
    if (this.last === null) return Math.max(0, nominal);
    const natural = this.last + HOP;
    let best = Math.max(0, nominal);
    let bestScore = -Infinity;
    for (let d = -SEARCH; d <= SEARCH; d += DECIMATE) {
      const at = nominal + d;
      if (at < 0 || at + WINDOW > this.mono.length) continue;
      let score = 0;
      for (let i = 0; i < WINDOW; i += DECIMATE) score += (this.mono[at + i] ?? 0) * (this.mono[natural + i] ?? 0);
      if (score > bestScore) {
        bestScore = score;
        best = at;
      }
    }
    return best;
  }
}

export function monoMix(channels: readonly Float32Array[]): Float32Array {
  const mono = new Float32Array(channels[0]?.length ?? 0);
  for (const ch of channels) for (let i = 0; i < mono.length; i++) mono[i] = (mono[i] ?? 0) + (ch[i] ?? 0);
  return mono;
}
